/**
 * The single owner of the access token.
 *
 * STORAGE DECISION: `localStorage`, so a reload does not need a round trip
 * before the first request. The access token is short-lived (minutes) and is
 * only a cache of the server-side SESSION, whose secret lives in an httpOnly
 * cookie no script can read. A stolen access token therefore works for minutes,
 * not for the life of the session, and signing out ends it on the server.
 *
 * Keeping every read and write in this one module means it is the only place
 * to audit for token handling.
 *
 * The value is mirrored in memory so the request path never depends on
 * `localStorage` being available - it throws in some privacy modes - and other
 * tabs are followed through the `storage` event, so a refresh in one tab is
 * picked up by all of them and signing out in one signs out the rest.
 */

const STORAGE_KEY = 'orbit-sync.access-token';

/**
 * `notice` says why the token went away: a message for the sign-in page, or
 * `null` for a deliberate sign-out that needs no explanation. `undefined` when
 * the token was merely replaced.
 */
type TokenListener = (token: string | null, notice?: string | null) => void;

const listeners = new Set<TokenListener>();

let cached: string | null = null;
let loaded = false;

function readFromStorage(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // Storage disabled or unavailable; the in-memory copy still works for this tab.
    return null;
  }
}

function writeToStorage(token: string | null): void {
  try {
    if (token === null) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, token);
    }
  } catch {
    // Non-fatal: the session survives in memory until the tab closes.
  }
}

function notify(token: string | null, notice?: string | null): void {
  for (const listener of listeners) {
    listener(token, notice);
  }
}

export function getAccessToken(): string | null {
  if (!loaded) {
    cached = readFromStorage();
    loaded = true;
  }

  return cached;
}

export function setAccessToken(token: string | null, notice?: string | null): void {
  if (getAccessToken() === token) {
    return;
  }

  cached = token;
  loaded = true;
  writeToStorage(token);
  notify(token, notice);
}

/** Drops the token. `notice` explains an involuntary sign-out; `null` for a deliberate one. */
export function clearAccessToken(notice?: string | null): void {
  setAccessToken(null, notice);
}

/**
 * Notifies when the token changes.
 *
 * The auth store subscribes to this so that a token cleared from outside React -
 * the API client learning the session has ended - still resets the UI to a
 * logged-out state, with the reason.
 */
export function subscribeToAccessToken(listener: TokenListener): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

// Another tab refreshed the token or signed out.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== STORAGE_KEY || event.newValue === cached) {
      return;
    }

    cached = event.newValue;
    loaded = true;
    notify(cached, cached === null ? null : undefined);
  });
}
